import type { BusinessType, Persona, SignalKind } from './enums';
import type { MergeFields } from './templates';
import { TEMPLATE_FIELDS, renderTemplate } from './templates';

/** The message library (D88) — which message fits a merchant, and how it renders. Shared by the
 *  Worker and the device so a draft reads the same wherever it is produced. Nothing here sends
 *  anything: every result is copy for Artur to send himself. */

/** A lead's business type maps to the persona the library is written for. `other` is the catch-all
 *  the library calls counter services: anything taking payment across a counter that is not food,
 *  retail inventory or appointment-based. */
const PERSONA_BY_TYPE: Record<BusinessType, Persona> = {
  restaurant: 'restaurant',
  cafe: 'restaurant',
  pizzeria: 'restaurant',
  bar: 'restaurant',
  retail: 'retail',
  salon: 'salon',
  other: 'counter',
};

export const personaForBusinessType = (type: BusinessType): Persona => PERSONA_BY_TYPE[type];

/** A new-business signal carries a `kind`, not a business type. `service` covers both salons and
 *  counter work, and the filing rarely says which: the library's salon copy asks about chairs and
 *  booking, which is wrong for a locksmith, so an unresolved `service` reads as counter. */
const PERSONA_BY_KIND: Record<SignalKind, Persona> = {
  food_drink: 'restaurant',
  food_truck: 'restaurant',
  retail: 'retail',
  service: 'counter',
};

export const personaForSignalKind = (kind: SignalKind): Persona => PERSONA_BY_KIND[kind];

/** "Salon" is a guess the filing cannot settle; the name usually can. Only words that name the
 *  trade count — a "nail bar" is a salon, a "bar" is not. */
const SALON_WORDS =
  /\b(salon|barber|barbershop|hair|nails?|nail bar|spa|lash|brow|beauty|waxing|blow dry|blowdry)\b/i;

export const personaForSignal = (kind: SignalKind, businessName: string): Persona => {
  const base = personaForSignalKind(kind);
  if (base === 'counter' && SALON_WORDS.test(businessName)) return 'salon';
  return base;
};

/** A route stop with no lead behind it knows only what Google says it is. These are the Places
 *  types the day pool and the corridor scout ask for; anything else falls to counter services. */
const PERSONA_BY_PLACE_TYPE: Record<string, Persona> = {
  restaurant: 'restaurant',
  cafe: 'restaurant',
  coffee_shop: 'restaurant',
  bakery: 'restaurant',
  bar: 'restaurant',
  meal_takeaway: 'restaurant',
  meal_delivery: 'restaurant',
  food: 'restaurant',
  store: 'retail',
  clothing_store: 'retail',
  convenience_store: 'retail',
  book_store: 'retail',
  furniture_store: 'retail',
  gift_shop: 'retail',
  jewelry_store: 'retail',
  pet_store: 'retail',
  shoe_store: 'retail',
  sporting_goods_store: 'retail',
  hair_care: 'salon',
  hair_salon: 'salon',
  beauty_salon: 'salon',
  barber_shop: 'salon',
  nail_salon: 'salon',
  spa: 'salon',
};

/** The most specific type wins: Google lists `store` beside `clothing_store`, and `food` beside
 *  `restaurant`, so the generic ones are only read when nothing more specific is there. */
const GENERIC_TYPES = new Set(['store', 'food', 'point_of_interest', 'establishment']);

export const personaForPlaceTypes = (types: readonly string[]): Persona => {
  const specific = types.find((t) => !GENERIC_TYPES.has(t) && t in PERSONA_BY_PLACE_TYPE);
  if (specific) return PERSONA_BY_PLACE_TYPE[specific] as Persona;
  const generic = types.find((t) => t in PERSONA_BY_PLACE_TYPE);
  return generic ? (PERSONA_BY_PLACE_TYPE[generic] as Persona) : 'counter';
};

/** The library's rule for a missing owner name: open with "Hi —" and nothing else. Never "Hi there",
 *  never "Dear Owner". Only the opening is touched; a later `{first_name}` renders empty. */
export const GREETING_RULE = /^\{first_name\}(\s*—)/;

export const applyGreeting = (body: string, firstName: string | null): string =>
  firstName ? body : body.replace(GREETING_RULE, 'Hi$1');

export interface RenderedMessage {
  subject: string | null;
  body: string;
}

/** Render a library message for one merchant. A merge field with no value is left as the library's
 *  own bracket (`[city]`) rather than silently emptied, so an unfilled draft is visibly unfilled —
 *  as are the `[[PLACEHOLDERS]]` only Artur can fill. */
export const renderMessage = (
  message: { subject: string | null; body: string },
  fields: MergeFields,
): RenderedMessage => {
  const filled: MergeFields = {};
  for (const field of TEMPLATE_FIELDS) {
    const value = fields[field];
    filled[field] = value === undefined || value === null || value === '' ? `[${field}]` : value;
  }
  const withGreeting = applyGreeting(message.body, fields.first_name?.trim() ? fields.first_name : null);
  return {
    subject: message.subject === null ? null : renderTemplate(message.subject, filled),
    body: renderTemplate(withGreeting, filled),
  };
};

/** The first name the library wants: the owner's first word, nothing invented. */
export const firstNameOf = (ownerName: string | null | undefined): string | null => {
  const first = (ownerName ?? '').trim().split(/\s+/)[0] ?? '';
  return first.length ? first : null;
};
